// Shared spreadsheet-parsing helpers for the Upload step.
//
// FMX's own export templates (and plenty of customer files) don't put the
// column headers on the first row: they carry merged section banners, blank
// spacer rows, and grouped sub-headers (weekday From/To pairs, rate Cost/Unit
// pairs) above and below the real field names. These helpers find the real
// header row, flatten grouped headers using the sheet's merge ranges, and
// guarantee every column has a unique non-empty name so no column silently
// collapses when rows become objects keyed by header.

const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?$/i;
const DATE_RE = /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/;
const NUMERIC_RE = /^-?[\d.,$%]+$/;

const looksLikeData = v => NUMERIC_RE.test(v) || TIME_RE.test(v) || DATE_RE.test(v);

// Returns the index of the most header-like row among the first `maxScan`
// rows of a string grid. Score = non-empty count × distinct ratio × non-data
// ratio. A later row only replaces the current pick when it outscores it by
// 1.5× AND every row above it is sparse (≤ half its fill) — a dense row above
// means the candidate is more likely the first data row, so ordinary sheets
// with headers on row 0 are never re-interpreted. The sparse-above guard is
// what prevents false positives, so the scan window just needs to comfortably
// exceed any realistic preamble depth.
export function detectHeaderRow(grid, maxScan = 30) {
  const limit = Math.min(maxScan, grid.length);
  const counts = [];
  const scores = [];
  for (let r = 0; r < limit; r++) {
    const cells = grid[r].filter(v => v !== "");
    counts.push(cells.length);
    if (!cells.length) { scores.push(0); continue; }
    const distinct = new Set(cells).size / cells.length;
    const texty = cells.filter(v => !looksLikeData(v)).length / cells.length;
    scores.push(cells.length * distinct * texty);
  }
  let headerRow = 0;
  for (let r = 1; r < limit; r++) {
    const sparseAbove = counts.slice(0, r).every(c => c <= counts[r] / 2);
    if (sparseAbove && scores[r] > scores[headerRow] * 1.5) headerRow = r;
  }
  return headerRow;
}

// Blank names become "Column N"; duplicates get " (2)", " (3)", … suffixes.
export function uniqueHeaders(rawNames) {
  const used = new Set();
  return rawNames.map((name, i) => {
    const base = String(name ?? "").replace(/\s+/g, " ").trim() || `Column ${i + 1}`;
    let candidate = base;
    for (let k = 2; used.has(candidate); k++) candidate = `${base} (${k})`;
    used.add(candidate);
    return candidate;
  });
}

// Builds {headers, rows} directly from a worksheet — no CSV round-trip.
// XLSX is passed in by the caller because App.js lazy-loads the xlsx module;
// importing it statically here would pull it into the main bundle.
export function tableFromWorksheet(XLSX, ws) {
  if (!ws) return { headers: [], rows: [], headerRow: 0, droppedColumns: 0 };

  // Keep blank rows: ws['!merges'] uses absolute sheet coordinates, and
  // dropping blank rows here would misalign every merge lookup below.
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
  if (!aoa.length) return { headers: [], rows: [], headerRow: 0, droppedColumns: 0 };

  const width = Math.max(...aoa.map(r => r.length));
  const grid = aoa.map(r => {
    const cells = r.map(v => String(v ?? "").trim());
    while (cells.length < width) cells.push("");
    return cells;
  });

  const merges = ws["!merges"] || [];
  const mergedVal = (r, c) => {
    for (const m of merges) {
      if (r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c) return grid[m.s.r]?.[m.s.c] ?? "";
    }
    return grid[r]?.[c] ?? "";
  };
  const inHorizMerge = (row, c) => merges.some(m =>
    m.s.r === row && m.e.r === row && m.e.c > m.s.c && c >= m.s.c && c <= m.e.c);

  const headerRow = detectHeaderRow(grid);

  // The row below the header counts as part of the header block when its
  // values sit only under horizontally-merged or blank header cells (e.g.
  // FMX exports: weekday groups over From/To pairs, rate types over
  // Cost/Unit pairs).
  const next = grid[headerRow + 1];
  let hasSubHeader = false;
  if (next) {
    const nonEmptyCols = next.map((v, c) => (v !== "" ? c : -1)).filter(c => c >= 0);
    hasSubHeader = nonEmptyCols.length > 0 &&
      nonEmptyCols.every(c => inHorizMerge(headerRow, c) || grid[headerRow][c] === "");
  }

  const collapse = s => s.replace(/\s+/g, " ").trim();
  const rawNames = [];
  for (let c = 0; c < width; c++) {
    const parent = collapse(mergedVal(headerRow, c));
    // Vertically-merged simple fields resolve to the same value on both
    // rows — only compose when the sub-header adds information.
    const sub = hasSubHeader ? collapse(mergedVal(headerRow + 1, c)) : "";
    rawNames.push(sub && sub !== parent ? (parent ? `${parent} ${sub}` : sub) : parent);
  }
  const headers = uniqueHeaders(rawNames);

  const dataStart = headerRow + (hasSubHeader ? 2 : 1);
  const rows = grid.slice(dataStart)
    .filter(r => r.some(v => v !== ""))
    .map(cells => Object.fromEntries(headers.map((h, j) => [h, cells[j] ?? ""])));

  // Drop columns with no data at all — FMX templates carry dozens of blank
  // placeholder columns (<Custom field>, unused rate pairs).
  const kept = headers.filter(h => rows.some(row => row[h] !== ""));
  if (!kept.length || kept.length === headers.length) {
    return { headers, rows, headerRow, droppedColumns: 0 };
  }
  return {
    headers: kept,
    rows: rows.map(row => Object.fromEntries(kept.map(h => [h, row[h]]))),
    headerRow,
    droppedColumns: headers.length - kept.length,
  };
}
